import TimeExtent from '@arcgis/core/TimeExtent';
import TimeSlider from '@arcgis/core/widgets/TimeSlider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Box } from '@rsos/base-components';
import { selectActiveLayers } from '@rsos/reduxDataStore';
import type { TimeSliderComponentProps, TimeLayer } from './TimeSlider.types';
import { TimeSliderContainer } from './TimeSliderContainer';
import { isGroupLayer, findFullTimeExtent } from './TimeSliderHelpers';

const TimeSliderComponent = ({ map }: TimeSliderComponentProps) => {
  const timeSliderRef = useRef<TimeSlider | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [containerReady, setContainerReady] = useState<boolean>(false);

  const [childLoadVersion, setChildLoadVersion] = useState(0);
  const activeLayers = useSelector(selectActiveLayers) as TimeLayer[];

  // Bump version when async child layers finish loading so filters re-evaluate
  useEffect(() => {
    let cancelled = false;
    const promises: Promise<unknown>[] = [];

    for (const layer of activeLayers) {
      if (isGroupLayer(layer.gisLayer)) {
        for (const child of layer.gisLayer.layers.toArray()) {
          if (!child.loaded) {
            promises.push(child.when().catch(() => undefined));
          }
        }
      }
    }

    if (promises.length > 0) {
      Promise.all(promises).then(() => {
        if (!cancelled) setChildLoadVersion(v => v + 1);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [activeLayers]);

  const activeTimeEnabledLayers = useMemo(() => {
    return activeLayers.filter(
      layer => findFullTimeExtent(layer.gisLayer) !== null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayers, childLoadVersion]);

  const hasTimeLayers = activeTimeEnabledLayers.length > 0;

  const selectedLayer = activeTimeEnabledLayers.find(
    layer => layer.id === selectedLayerId,
  );

  // Cached to return a stable reference when dates haven't changed
  const timeExtentCacheRef = useRef<{
    startMs: number;
    endMs: number;
    extent: TimeExtent;
  } | null>(null);

  const timeExtent = useMemo(() => {
    if (!selectedLayer) {
      timeExtentCacheRef.current = null;
      return null;
    }
    const found = findFullTimeExtent(selectedLayer.gisLayer);
    if (!found) {
      timeExtentCacheRef.current = null;
      return null;
    }

    const startMs = found.start.getTime();
    const endMs = found.end.getTime();

    if (
      timeExtentCacheRef.current &&
      timeExtentCacheRef.current.startMs === startMs &&
      timeExtentCacheRef.current.endMs === endMs
    ) {
      return timeExtentCacheRef.current.extent;
    }

    try {
      const extent = new TimeExtent({ start: found.start, end: found.end });
      timeExtentCacheRef.current = { startMs, endMs, extent };
      return extent;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create TimeExtent:', error);
      timeExtentCacheRef.current = null;
      return null;
    }
  }, [selectedLayer]);

  // Auto-select first time-enabled layer or reset when list changes
  useEffect(() => {
    if (!hasTimeLayers) {
      setSelectedLayerId(null);
      return;
    }
    if (
      !selectedLayerId ||
      !activeTimeEnabledLayers.some(layer => layer.id === selectedLayerId)
    ) {
      setSelectedLayerId(activeTimeEnabledLayers[0].id);
    }
  }, [hasTimeLayers, activeTimeEnabledLayers, selectedLayerId]);

  // Manage DOM container on map UI
  useEffect(() => {
    if (!map?.mapInstance?.view?.ui) return;

    if (!hasTimeLayers) {
      if (mapContainerRef.current && map?.mapInstance?.view?.ui) {
        map.mapInstance.view.ui.remove(mapContainerRef.current);
        mapContainerRef.current = null;
      }
      setContainerReady(false);
      return;
    }

    const container = document.createElement('div');
    mapContainerRef.current = container;
    map.mapInstance?.view?.ui?.add(container, 'bottom-right');
    setContainerReady(true);

    return () => {
      const viewUI = map?.mapInstance?.view?.ui;
      if (mapContainerRef.current && viewUI) {
        try {
          viewUI.remove(mapContainerRef.current);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
        mapContainerRef.current = null;
      }
      setContainerReady(false);
    };
  }, [map, hasTimeLayers]);

  // Create widget once when container and view are ready
  useEffect(() => {
    const view = map?.mapInstance?.view;
    if (!view || !contentRef.current || !hasTimeLayers || !containerReady) {
      return;
    }
    if (timeSliderRef.current) return;

    const containerEl = contentRef.current;
    const extent = timeExtent;

    try {
      const timeSlider = new TimeSlider({
        container: containerEl,
        view,
        mode: 'time-window',
        timeVisible: true,
        loop: false,
        playRate: 1000,
        ...(extent ? { fullTimeExtent: extent, timeExtent: extent } : {}),
        visible: true,
      });
      timeSliderRef.current = timeSlider;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    return () => {
      if (timeSliderRef.current?.destroy) {
        try {
          timeSliderRef.current.destroy();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
        timeSliderRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map?.mapInstance?.view, hasTimeLayers, containerReady]);

  // Update widget time extent in place without recreating
  useEffect(() => {
    const widget = timeSliderRef.current;
    if (!widget || !timeExtent) return;

    try {
      if (widget.destroyed) return;
      widget.fullTimeExtent = timeExtent;
      widget.timeExtent = timeExtent;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [timeExtent]);

  if (!hasTimeLayers || !containerReady || !mapContainerRef.current) {
    return null;
  }

  const portalContainer = mapContainerRef.current;
  if (!portalContainer) return null;
  return createPortal(
    <TimeSliderContainer
      timeEnabledLayers={activeTimeEnabledLayers}
      isExpanded={isExpanded}
      selectedLayerId={selectedLayerId}
      onLayerChange={setSelectedLayerId}
      onToggle={() => setIsExpanded(prev => !prev)}
    >
      <Box ref={contentRef} />
    </TimeSliderContainer>,
    portalContainer,
  );
};

export default TimeSliderComponent;
